import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from '../context/TranslationContext';
import { Navbar, Footer } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import SEO from '../components/SEO';
import { Progress } from '../components/ui/progress';
import { Skeleton } from '../components/ui/skeleton';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
  DialogClose,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { categoryLabel, safeArray, safeNum } from '../lib/utils';
import SafeHTML from '../components/SafeHTML';
import { 
  Play, Clock, BookOpen, Lock, Check, ChevronRight,
  Award, Star, Crown, ThumbsUp, MessageSquare, Edit, Trash2, User
} from 'lucide-react';

import { getApiBaseUrl } from '@/lib/apiBase';
const API_URL = getApiBaseUrl();

const CourseDetail = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const { user, isPremium, token } = useAuth();
  const { t, tFormat } = useTranslation();
  const [course, setCourse] = useState(null);
  const [lessons, setLessons] = useState([]);
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Reviews state
  const [reviews, setReviews] = useState([]);
  const [ratingSummary, setRatingSummary] = useState(null);
  const [reviewsLoading, setReviewsLoading] = useState(true);
  const [reviewSort, setReviewSort] = useState('recent');
  const [reviewPage, setReviewPage] = useState(1);
  const [totalReviewPages, setTotalReviewPages] = useState(1);
  
  // Review form state
  const [showReviewDialog, setShowReviewDialog] = useState(false);
  const [reviewRating, setReviewRating] = useState(5);
  const [reviewTitle, setReviewTitle] = useState('');
  const [reviewContent, setReviewContent] = useState('');
  const [submittingReview, setSubmittingReview] = useState(false);
  const [editingReview, setEditingReview] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        
        const [courseRes, lessonsRes] = await Promise.all([
          axios.get(`${API_URL}/courses/${courseId}`, { headers }),
          axios.get(`${API_URL}/courses/${courseId}/lessons`, { headers }).catch(() => ({ data: [] }))
        ]);
        
        setCourse(courseRes.data);
        const lessonList = courseRes.data?.lessons ?? lessonsRes.data;
        setLessons(safeArray(Array.isArray(lessonList) ? lessonList : []));
        
        // Pre-fill if user has existing review
        if (courseRes.data.user_review) {
          setReviewRating(courseRes.data.user_review.rating);
          setReviewTitle(courseRes.data.user_review.title || '');
          setReviewContent(courseRes.data.user_review.content || '');
          setEditingReview(courseRes.data.user_review);
        }
        
        if (token) {
          try {
            const progressRes = await axios.get(`${API_URL}/progress/${courseId}`, { headers });
            setProgress(progressRes.data);
          } catch (err) {}
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [courseId, token]);

  useEffect(() => {
    fetchReviews();
  }, [courseId, reviewSort, reviewPage]);

  const fetchReviews = async () => {
    setReviewsLoading(true);
    try {
      const [reviewsRes, summaryRes] = await Promise.all([
        axios.get(`${API_URL}/courses/${courseId}/reviews?sort=${reviewSort}&page=${reviewPage}&limit=5`),
        axios.get(`${API_URL}/courses/${courseId}/rating-summary`)
      ]);
      setReviews(safeArray(reviewsRes.data?.reviews));
      setTotalReviewPages(reviewsRes.data.pages || 1);
      setRatingSummary(summaryRes.data);
    } catch (err) {
      console.error('Error fetching reviews:', err);
    } finally {
      setReviewsLoading(false);
    }
  };

  const handleSubmitReview = async () => {
    if (!token) {
      navigate('/login');
      return;
    }
    
    setSubmittingReview(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      if (editingReview) {
        await axios.put(`${API_URL}/reviews/${editingReview.id}`, {
          rating: reviewRating,
          title: reviewTitle,
          content: reviewContent
        }, { headers });
        toast.success(t('toast_review_updated', 'Review updated successfully!'));
      } else {
        await axios.post(`${API_URL}/courses/${courseId}/reviews`, {
          rating: reviewRating,
          title: reviewTitle,
          content: reviewContent
        }, { headers });
        toast.success(t('toast_review_submitted', 'Review submitted successfully!'));
      }
      
      setShowReviewDialog(false);
      fetchReviews();
      // Refresh course data to get updated rating
      const courseRes = await axios.get(`${API_URL}/courses/${courseId}`, { headers });
      setCourse(courseRes.data);
      if (courseRes.data.user_review) {
        setEditingReview(courseRes.data.user_review);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || t('toast_review_submit_fail', 'Failed to submit review'));
    } finally {
      setSubmittingReview(false);
    }
  };

  const handleDeleteReview = async () => {
    if (!editingReview || !token) return;
    
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(`${API_URL}/reviews/${editingReview.id}`, { headers });
      toast.success(t('toast_review_deleted', 'Review deleted'));
      setEditingReview(null);
      setReviewRating(5);
      setReviewTitle('');
      setReviewContent('');
      fetchReviews();
      // Refresh course data
      const courseRes = await axios.get(`${API_URL}/courses/${courseId}`, { headers });
      setCourse(courseRes.data);
    } catch (err) {
      toast.error(t('toast_review_delete_fail', 'Failed to delete review'));
    }
  };

  const handleMarkHelpful = async (reviewId) => {
    if (!token) {
      navigate('/login');
      return;
    }
    
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API_URL}/reviews/${reviewId}/helpful`, {}, { headers });
      fetchReviews();
    } catch (err) {
      toast.error(t('toast_review_helpful_fail', 'Failed to mark review'));
    }
  };

  const isLessonAccessible = (lesson, index) => {
    // Server is authoritative on premium access (see GET /courses/{id}) —
    // fall back to the old client-side heuristic only if `locked` is absent
    // (e.g. an older cached response shape).
    if (typeof lesson.locked === 'boolean') return !lesson.locked;
    if (lesson.is_free) return true;
    if (isPremium) return true;
    if (course?.is_free) return true;
    return false;
  };

  const isViewLimitLocked = (lesson) => !!lesson.view_limit_locked;

  const renderStars = (rating, size = 'w-5 h-5', interactive = false, onSelect = null) => {
    const r = safeNum(rating, 0);
    return (
      <div className="flex items-center gap-0.5">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`${size} ${
              star <= r
                ? 'fill-amber-400 text-amber-400'
                : 'text-gray-300'
            } ${interactive ? 'cursor-pointer hover:scale-110 transition-transform' : ''}`}
            onClick={() => interactive && onSelect && onSelect(star)}
          />
        ))}
      </div>
    );
  };

  const renderRatingDistribution = () => {
    if (!ratingSummary) return null;
    const dist = ratingSummary.rating_distribution;
    if (!dist || typeof dist !== 'object') return null;
    const counts = Object.values(dist).map((v) => safeNum(v, 0));
    const maxCount = counts.length ? Math.max(...counts) : 0;
    
    return (
      <div className="space-y-2">
        {[5, 4, 3, 2, 1].map((rating) => {
          const count = safeNum(dist[rating.toString()], 0);
          const percentage = maxCount > 0 ? (count / maxCount) * 100 : 0;
          
          return (
            <div key={rating} className="flex items-center gap-2 text-sm">
              <span className="w-3">{rating}</span>
              <Star className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />
              <div className="flex-1 h-2 bg-secondary rounded-full overflow-hidden">
                <div 
                  className="h-full bg-amber-400 rounded-full transition-all"
                  style={{ width: `${percentage}%` }}
                />
              </div>
              <span className="w-8 text-muted-foreground text-right">{count}</span>
            </div>
          );
        })}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <main className="pt-20">
          <div className="max-w-7xl mx-auto px-6 md:px-12 py-12">
            <Skeleton className="h-12 w-2/3 mb-4" />
            <Skeleton className="h-6 w-1/2 mb-8" />
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2 space-y-4">
                {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-20" />)}
              </div>
              <Skeleton className="h-64" />
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (!course) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <main className="pt-20">
          <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 text-center">
            <h1 className="text-2xl font-semibold mb-4">
              {t('course_not_found', 'Course not found')}
            </h1>
            <Button onClick={() => navigate('/courses')}>
              {t('course_back_catalog', 'Back to Courses')}
            </Button>
          </div>
        </main>
      </div>
    );
  }

  const seoDescription = typeof course.description === 'string'
    ? course.description.replace(/<[^>]*>/g, '').slice(0, 155)
    : '';

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title={`${course.title} — Interior Training`}
        description={seoDescription}
        keywords={`${course.title}, interior technician course, interior technical course India, ${course.category || ''}`}
        url={`/courses/${course.id}`}
        type="article"
      />
      <Navbar />

      <main className="pt-20">
        {/* Hero */}
        <section className="bg-secondary/30 py-12" data-testid="course-hero">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
              <Link to="/courses" className="hover:text-accent">
                {t('course_breadcrumb_courses', 'Courses')}
              </Link>
              <ChevronRight className="w-4 h-4" />
              <span className="capitalize">{categoryLabel(course.category)}</span>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2">
                <Badge variant="secondary" className="mb-4 capitalize">{categoryLabel(course.category)}</Badge>
                <h1 className="text-4xl md:text-5xl font-serif font-medium mb-4" data-testid="course-title">
                  {course.title}
                </h1>
                <SafeHTML html={course.description} className="text-lg text-muted-foreground mb-6" />
                <div className="flex flex-wrap items-center gap-6 text-sm">
                  <span className="flex items-center gap-2">
                    <BookOpen className="w-5 h-5 text-accent" />
                    {safeNum(course.lesson_count, lessons.length)} {t('course_meta_lessons', 'lessons')}
                  </span>
                  <span className="flex items-center gap-2">
                    <Clock className="w-5 h-5 text-accent" />
                    {safeArray(lessons).reduce((acc, l) => acc + safeNum(l?.duration_minutes, 0), 0)}{' '}
                    {t('course_meta_min', 'min')}
                  </span>
                  <span className="flex items-center gap-2">
                    <Award className="w-5 h-5 text-accent" />
                    {t('course_meta_certificate', 'Certificate')}
                  </span>
                  {safeNum(course.review_count, 0) > 0 && (
                    <span className="flex items-center gap-2">
                      {renderStars(safeNum(course.average_rating, 0), 'w-4 h-4')}
                      <span className="font-medium">{safeNum(course.average_rating, 0)}</span>
                      <span className="text-muted-foreground">
                        ({safeNum(course.review_count, 0)} {t('course_reviews_count', 'reviews')})
                      </span>
                    </span>
                  )}
                </div>
              </div>
              <div className="hidden lg:block">
                <img 
                  src={course.thumbnail_url || 'https://images.unsplash.com/photo-1713652492101-4705c38e9b58?crop=entropy&cs=srgb&fm=jpg&q=85'}
                  alt={course.title}
                  className="rounded-xl shadow-lg aspect-[4/3] object-cover"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Content */}
        <section className="py-12">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Main Content */}
              <div className="lg:col-span-2 space-y-10">
                {/* Lessons */}
                {(course?.enable_content !== false) && (
                <div data-testid="lessons-list">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-2xl font-serif font-medium">
                      {t('course_content_heading', 'Course Content')}
                    </h2>
                    {progress && (
                      <div className="flex items-center gap-3">
                        <Progress value={progress.progress_percent} className="w-32 h-2" />
                        <span className="text-sm text-muted-foreground">
                          {Math.round(progress.progress_percent)}
                          {t('course_progress_complete', '% complete')}
                        </span>
                      </div>
                    )}
                  </div>
                  
                  <div className="space-y-3">
                    {lessons.map((lesson, index) => {
                      const accessible = isLessonAccessible(lesson, index);
                      const completed = !!lesson.is_completed;
                      const viewLocked = accessible && isViewLimitLocked(lesson);

                      return (
                        <Card
                          key={lesson.id}
                          className={`transition-all ${accessible ? 'hover:shadow-md cursor-pointer' : 'opacity-60'}`}
                          onClick={() => accessible && navigate(`/lessons/${lesson.id}`)}
                          onTouchEnd={(e) => {
                            if (!accessible) return;
                            e.preventDefault();
                            navigate(`/lessons/${lesson.id}`);
                          }}
                          role={accessible ? 'button' : undefined}
                          tabIndex={accessible ? 0 : undefined}
                          onKeyDown={(e) => {
                            if (accessible && (e.key === 'Enter' || e.key === ' ')) {
                              e.preventDefault();
                              navigate(`/lessons/${lesson.id}`);
                            }
                          }}
                          style={accessible ? { WebkitTapHighlightColor: 'transparent', touchAction: 'manipulation' } : undefined}
                          data-testid={`lesson-${lesson.id}`}
                        >
                          <CardContent className="p-4 flex items-center gap-4">
                            <div className={`w-12 h-12 rounded-full flex items-center justify-center shrink-0 ${
                              completed ? 'bg-green-500/10 text-green-500' :
                              accessible && !viewLocked ? 'bg-accent/10 text-accent' : 'bg-muted text-muted-foreground'
                            }`}>
                              {viewLocked ? <Lock className="w-5 h-5" /> :
                               completed ? <Check className="w-5 h-5" /> :
                               accessible ? <Play className="w-5 h-5" /> : <Lock className="w-5 h-5" />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="text-sm text-muted-foreground">
                                  {t('course_lesson_label', 'Lesson')} {index + 1}
                                </span>
                                {lesson.is_free && (
                                  <Badge variant="secondary" className="text-xs">
                                    {t('label_free', 'Free')}
                                  </Badge>
                                )}
                                {completed && !viewLocked && (
                                  <span className="text-xs font-semibold text-green-600" data-testid="lesson-rewatch-label">
                                    {t('course_lesson_rewatch', 'Completed · Rewatch')}
                                  </span>
                                )}
                                {viewLocked && (
                                  <Badge variant="outline" className="text-xs" data-testid="view-limit-badge">
                                    {tFormat('lesson_study_again', { price: lesson.reattempt_price }, 'Study Again — ₹{price}')}
                                  </Badge>
                                )}
                              </div>
                              <h3 className="font-medium truncate">{lesson.title}</h3>
                            </div>
                            <div className="text-sm text-muted-foreground shrink-0">
                              {lesson.duration_minutes} {t('course_meta_min', 'min')}
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                </div>
                )}

                {/* Reviews Section */}
                {(course?.enable_reviews !== false) && (
                <div data-testid="reviews-section">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-2xl font-serif font-medium">
                      {t('course_reviews_heading', 'Reviews')}
                    </h2>
                    <Dialog open={showReviewDialog} onOpenChange={setShowReviewDialog}>
                      <DialogTrigger asChild>
                        <Button variant="outline" className="gap-2">
                          {editingReview ? <Edit className="w-4 h-4" /> : <MessageSquare className="w-4 h-4" />}
                          {editingReview
                            ? t('course_review_edit', 'Edit Review')
                            : t('course_review_write', 'Write a Review')}
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="sm:max-w-[500px]">
                        <DialogHeader>
                          <DialogTitle>
                            {editingReview
                              ? t('course_review_dialog_edit_title', 'Edit Your Review')
                              : t('course_review_dialog_title', 'Write a Review')}
                          </DialogTitle>
                          <DialogDescription>
                            {t('course_review_dialog_desc', 'Share your experience with this course')}
                          </DialogDescription>
                        </DialogHeader>
                        <div className="space-y-4 py-4">
                          <div>
                            <label className="text-sm font-medium mb-2 block">
                              {t('course_review_rating_label', 'Rating')}
                            </label>
                            <div className="flex items-center gap-1">
                              {renderStars(reviewRating, 'w-8 h-8', true, setReviewRating)}
                              <span className="ml-2 text-lg font-medium">{reviewRating}/5</span>
                            </div>
                          </div>
                          <div>
                            <label className="text-sm font-medium mb-2 block">
                              {t('course_review_title_label', 'Title (optional)')}
                            </label>
                            <Input
                              placeholder={t('course_review_title_placeholder', 'Summarize your experience')}
                              value={reviewTitle}
                              onChange={(e) => setReviewTitle(e.target.value)}
                            />
                          </div>
                          <div>
                            <label className="text-sm font-medium mb-2 block">
                              {t('course_review_body_label', 'Review')}
                            </label>
                            <Textarea
                              placeholder={t(
                                'course_review_body_placeholder',
                                'Tell others about your experience with this course...'
                              )}
                              value={reviewContent}
                              onChange={(e) => setReviewContent(e.target.value)}
                              rows={4}
                            />
                          </div>
                        </div>
                        <DialogFooter className="flex justify-between">
                          {editingReview && (
                            <Button
                              variant="destructive"
                              onClick={handleDeleteReview}
                              className="mr-auto"
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              {t('course_review_delete', 'Delete')}
                            </Button>
                          )}
                          <div className="flex gap-2">
                            <DialogClose asChild>
                              <Button variant="outline">{t('course_review_cancel', 'Cancel')}</Button>
                            </DialogClose>
                            <Button onClick={handleSubmitReview} disabled={submittingReview}>
                              {submittingReview
                                ? t('course_review_submitting', 'Submitting...')
                                : editingReview
                                  ? t('course_review_update', 'Update')
                                  : t('course_review_submit', 'Submit')}
                            </Button>
                          </div>
                        </DialogFooter>
                      </DialogContent>
                    </Dialog>
                  </div>

                  {/* Rating Summary */}
                  {ratingSummary && ratingSummary.total_reviews > 0 && (
                    <Card className="mb-6">
                      <CardContent className="p-6">
                        <div className="flex flex-col md:flex-row gap-8">
                          <div className="text-center md:text-left">
                            <div className="text-5xl font-bold mb-2">{ratingSummary.average_rating}</div>
                            {renderStars(ratingSummary.average_rating)}
                            <p className="text-sm text-muted-foreground mt-2">
                              {ratingSummary.total_reviews} {t('course_reviews_total', 'reviews')}
                            </p>
                          </div>
                          <div className="flex-1">
                            {renderRatingDistribution()}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Reviews List */}
                  <div className="flex items-center justify-between mb-4">
                    <Select value={reviewSort} onValueChange={setReviewSort}>
                      <SelectTrigger className="w-[180px]">
                        <SelectValue placeholder={t('course_sort_by', 'Sort by')} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="recent">
                          {t('course_sort_recent', 'Most Recent')}
                        </SelectItem>
                        <SelectItem value="helpful">
                          {t('course_sort_helpful', 'Most Helpful')}
                        </SelectItem>
                        <SelectItem value="highest">
                          {t('course_sort_highest', 'Highest Rated')}
                        </SelectItem>
                        <SelectItem value="lowest">
                          {t('course_sort_lowest', 'Lowest Rated')}
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {reviewsLoading ? (
                    <div className="space-y-4">
                      {[1, 2, 3].map(i => (
                        <Card key={i}>
                          <CardContent className="p-6">
                            <Skeleton className="h-4 w-32 mb-2" />
                            <Skeleton className="h-4 w-full mb-2" />
                            <Skeleton className="h-4 w-3/4" />
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  ) : reviews.length === 0 ? (
                    <Card>
                      <CardContent className="p-12 text-center">
                        <MessageSquare className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
                        <h3 className="text-lg font-medium mb-2">
                          {t('course_no_reviews_title', 'No reviews yet')}
                        </h3>
                        <p className="text-muted-foreground mb-4">
                          {t('course_no_reviews_hint', 'Be the first to share your experience!')}
                        </p>
                        <Button onClick={() => setShowReviewDialog(true)}>
                          {t('course_review_write', 'Write a Review')}
                        </Button>
                      </CardContent>
                    </Card>
                  ) : (
                    <div className="space-y-4">
                      {reviews.map((review) => (
                        <Card key={review.id} data-testid={`review-${review.id}`}>
                          <CardContent className="p-6">
                            <div className="flex items-start justify-between mb-3">
                              <div>
                                <div className="flex items-center gap-3 mb-1">
                                  <div className="w-8 h-8 rounded-full bg-accent/10 flex items-center justify-center">
                                    <User className="w-4 h-4 text-accent" />
                                  </div>
                                  <span className="font-medium">{review.user_name}</span>
                                  {review.verified_purchase && (
                                    <Badge variant="secondary" className="text-xs">
                                      <Check className="w-3 h-3 mr-1" />
                                      {t('course_review_verified', 'Verified')}
                                    </Badge>
                                  )}
                                </div>
                                <div className="flex items-center gap-2">
                                  {renderStars(review.rating, 'w-4 h-4')}
                                  <span className="text-sm text-muted-foreground">
                                    {new Date(review.created_at).toLocaleDateString()}
                                  </span>
                                </div>
                              </div>
                            </div>
                            {review.title && (
                              <h4 className="font-medium mb-2">{review.title}</h4>
                            )}
                            {review.content && (
                              <p className="text-muted-foreground mb-4">{review.content}</p>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-muted-foreground hover:text-foreground"
                              onClick={() => handleMarkHelpful(review.id)}
                            >
                              <ThumbsUp className="w-4 h-4 mr-2" />
                              {t('course_review_helpful', 'Helpful')} ({review.helpful_count})
                            </Button>
                          </CardContent>
                        </Card>
                      ))}

                      {/* Review Pagination */}
                      {totalReviewPages > 1 && (
                        <div className="flex items-center justify-center gap-2 pt-4">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setReviewPage(p => Math.max(1, p - 1))}
                            disabled={reviewPage === 1}
                          >
                            {t('catalog_prev', 'Previous')}
                          </Button>
                          <span className="text-sm text-muted-foreground">
                            {t('course_review_page', 'Page')} {reviewPage} {t('course_review_of', 'of')}{' '}
                            {totalReviewPages}
                          </span>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setReviewPage(p => Math.min(totalReviewPages, p + 1))}
                            disabled={reviewPage === totalReviewPages}
                          >
                            {t('catalog_next', 'Next')}
                          </Button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                )}
              </div>

              {/* Sidebar */}
              <div className="lg:sticky lg:top-28 h-fit space-y-6" data-testid="course-sidebar">
                <Card>
                  <CardContent className="p-6">
                    {course.is_free ? (
                      <>
                        <div className="text-3xl font-bold text-green-600 mb-2">
                          {t('label_free', 'Free')}
                        </div>
                        <p className="text-muted-foreground text-sm mb-6">
                          {t('course_sidebar_free', 'Full access to all lessons')}
                        </p>
                      </>
                    ) : isPremium ? (
                      <>
                        <div className="flex items-center gap-2 mb-2">
                          <Crown className="w-5 h-5 text-amber-500" />
                          <span className="text-lg font-semibold">
                            {t('course_sidebar_premium_access', 'Premium Access')}
                          </span>
                        </div>
                        <p className="text-muted-foreground text-sm mb-6">
                          {t('course_sidebar_premium_full', 'You have full access to this course')}
                        </p>
                      </>
                    ) : (
                      <>
                        <div className="text-3xl font-bold mb-2">
                          {t('course_sidebar_premium_label', 'Premium')}
                        </div>
                        <p className="text-muted-foreground text-sm mb-6">
                          {t('course_sidebar_subscribe', 'Subscribe to access this course')}
                        </p>
                      </>
                    )}
                    
                    {user ? (
                      isPremium || course.is_free ? (
                        <Button 
                          className="w-full rounded-full bg-accent text-accent-foreground hover:bg-accent/90 mb-4"
                          onClick={() => lessons[0] && navigate(`/lessons/${lessons[0].id}`)}
                          data-testid="start-learning-btn"
                        >
                          <Play className="w-4 h-4 mr-2" />
                          {progress?.progress_percent > 0
                            ? t('course_continue_learning', 'Continue Learning')
                            : t('course_start_learning', 'Start Learning')}
                        </Button>
                      ) : (
                        <Button 
                          className="w-full rounded-full bg-accent text-accent-foreground hover:bg-accent/90 mb-4"
                          onClick={() => navigate('/pricing')}
                          data-testid="get-premium-btn"
                        >
                          {t('course_get_premium_access', 'Get Premium Access')}
                        </Button>
                      )
                    ) : (
                      <Button
                        className="w-full rounded-full bg-accent text-accent-foreground hover:bg-accent/90 mb-4"
                        onClick={() => { window.location.href = 'https://thachari.com/signup' }}
                        onTouchStart={() => { window.location.href = 'https://thachari.com/signup' }}
                        data-testid="signup-to-start-btn"
                      >
                        {t('course_signup_to_start', 'Sign Up to Start')}
                      </Button>
                    )}
                    
                    <div className="space-y-3 text-sm">
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <BookOpen className="w-4 h-4" />
                        {course.lesson_count} {t('course_sidebar_lessons_count', 'lessons')}
                      </div>
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Clock className="w-4 h-4" />
                        {lessons.reduce((acc, l) => acc + (l.duration_minutes || 0), 0)}{' '}
                        {t('course_sidebar_min_total', 'min total')}
                      </div>
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Award className="w-4 h-4" />
                        {t('course_sidebar_cert_on_complete', 'Certificate on completion')}
                      </div>
                      {course.review_count > 0 && (
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <Star className="w-4 h-4 fill-amber-400 text-amber-400" />
                          {course.average_rating} ({course.review_count}{' '}
                          {t('course_sidebar_reviews_inline', 'reviews')})
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {!isPremium && !course.is_free && (
                  <Card className="bg-accent/5 border-accent/20">
                    <CardContent className="p-6">
                      <div className="flex items-center gap-2 mb-2">
                        <Crown className="w-5 h-5 text-accent" />
                        <span className="font-semibold">
                          {t('course_premium_benefits', 'Premium Benefits')}
                        </span>
                      </div>
                      <ul className="space-y-2 text-sm text-muted-foreground">
                        <li className="flex items-center gap-2">
                          <Check className="w-4 h-4 text-green-500" />
                          {t('course_benefit_all_courses', 'Access all premium courses')}
                        </li>
                        <li className="flex items-center gap-2">
                          <Check className="w-4 h-4 text-green-500" />
                          {t('course_benefit_download', 'Download lessons offline')}
                        </li>
                        <li className="flex items-center gap-2">
                          <Check className="w-4 h-4 text-green-500" />
                          {t('course_benefit_certificates', 'Verified certificates')}
                        </li>
                      </ul>
                    </CardContent>
                  </Card>
                )}
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default CourseDetail;
